import type {
	PaymentMethodSharingDependentDto,
	PaymentMethodSharingDependentKind,
} from '@supply-mover-2-0/shared';

/**
 * PD-17009 — the words for sharing a saved payment method with an ownership
 * group, said once.
 *
 * The switch, its confirmations and the list of what depends on a shared
 * method appear in the CRM (add and edit panel, Payment Methods tab, ownership
 * screens) and on the storefront (add form, wallet). They live here, with no
 * React in them, so both say the same thing and the wording can be tested. A
 * warning about what stops working must not read differently depending on
 * which screen asked.
 *
 * Turning sharing ON asks nothing, for a card or a bank account alike (owner
 * decision, 2026-10-14). Only the changes that take a shared method away from
 * other accounts are confirmed.
 */

type TranslateFn = (
	key: string,
	params?: Record<string, string | number | null | undefined>,
) => string;

/** A confirmation's title, body and confirm-button text. */
export interface PaymentMethodSharingConfirmationModel {
	title: string;
	message: string;
	confirmText: string;
}

/**
 * The switch's label. Names the account only where the method is being saved
 * on an account other than the one the user is looking at (a card added from
 * an invoice's Pay panel can be saved to the invoice's account), so nobody
 * shares with a group they did not mean.
 */
export function paymentMethodSharingToggleLabel(
	t: TranslateFn,
	accountName?: string | null,
): string {
	return accountName != null && accountName !== ''
		? t("Share with every account in {{account}}'s ownership group", {
				account: t(accountName),
			})
		: t('Share with every account in this ownership group');
}

/** The help text under the switch. */
export function paymentMethodSharingToggleHelp(t: TranslateFn): string {
	return t(
		'Every account in the ownership group will be able to pay with this payment method. They see it masked, with the name of the account that shared it, and cannot edit, delete or stop sharing it.',
	);
}

/**
 * What one group of dependents is, in words: "2 payment schedules (Weekly
 * rent, Supplies)", "Billing collection method", "1 open order (10432)".
 * The translation layer interpolates but does not pluralize, so one and many
 * are separate keys.
 */
export function describePaymentMethodSharingDependent(
	dependent: Pick<PaymentMethodSharingDependentDto, 'kind' | 'count' | 'labels'>,
	t: TranslateFn,
): string {
	const { count } = dependent;
	const single = count === 1;
	const wording: Record<PaymentMethodSharingDependentKind, string> = {
		payment_schedule: single
			? t('1 payment schedule')
			: t('{{count}} payment schedules', { count }),
		autopay: single
			? t('1 autopay schedule')
			: t('{{count}} autopay schedules', { count }),
		cycle_billing: single
			? t('Cycle billing')
			: t('{{count}} cycle billing schedules', { count }),
		// One per account by construction: an account has one collection method.
		billing_collection_method: t('Billing collection method'),
		open_order: single
			? t('1 open order waiting to be charged')
			: t('{{count}} open orders waiting to be charged', { count }),
	};
	const names = dependent.labels.filter((label) => label !== '');

	if (names.length === 0) {
		return wording[dependent.kind];
	}

	const listed = names.map((name) => t(name)).join(', ');
	const more =
		count > names.length
			? `, ${t('and {{count}} more', { count: count - names.length })}`
			: '';

	return `${wording[dependent.kind]} (${listed}${more})`;
}

/**
 * The confirmation shown before sharing is turned off for a method, worded by
 * whether anything on another account still depends on it.
 */
export function stopSharingPaymentMethodConfirmation(
	t: TranslateFn,
	hasDependents: boolean,
): PaymentMethodSharingConfirmationModel {
	return {
		title: t('Stop sharing this payment method?'),
		message: hasDependents
			? t(
					'Other accounts in the ownership group will no longer be able to pay with it. The following will stop working, and their charges will fail until another payment method is chosen for them:',
				)
			: t(
					'Other accounts in the ownership group will no longer be able to pay with it. Nothing on another account is using it right now.',
				),
		confirmText: t('Stop sharing'),
	};
}

/**
 * The confirmation shown before a SHARED method is deleted. A method that is
 * not shared keeps the confirmation each screen already had.
 */
export function deleteSharedPaymentMethodConfirmation(
	t: TranslateFn,
	hasDependents: boolean,
): PaymentMethodSharingConfirmationModel {
	return {
		title: t('Delete this shared payment method?'),
		message: hasDependents
			? t(
					'This payment method is shared with the ownership group, and deleting it cannot be undone. The following on other accounts will stop working, and their charges will fail until another payment method is chosen for them:',
				)
			: t(
					'This payment method is shared with the ownership group, and deleting it cannot be undone. Nothing on another account is using it right now.',
				),
		confirmText: t('Delete'),
	};
}

/**
 * The warning added to the ownership screens' own confirmations when a
 * customer leaving a group takes a shared payment method with it, in either
 * direction.
 *
 * Worded about "an ownership", not "this ownership": the same sentence is
 * shown when a customer is removed from the ownership on screen, when that
 * ownership is deleted, and when a customer is moved INTO it from another
 * one, where the group being left is the other one.
 */
export function ownershipChangeSharingWarning(t: TranslateFn): string {
	return t(
		'A payment method shared through an ownership stops being shared with a customer that leaves it. The following will stop working, and their charges will fail until another payment method is chosen for them:',
	);
}

/**
 * Shown in place of the dependents list when it could not be loaded. The
 * change is still allowed (the decision is "warn, then allow"), but the dialog
 * must not suggest nothing depends on the method when it does not know.
 */
export function paymentMethodSharingDependentsUnavailable(t: TranslateFn): string {
	return t(
		'What uses this payment method on other accounts could not be checked. Any payment schedule, autopay, billing collection method or open order on another account that uses it will stop working.',
	);
}
